// src/types.ts
import { Dayjs } from "dayjs";
import { Timestamp } from "firebase/firestore";

export interface Service {
  id: string;
  title: string;
  description?: string;
  price: number;
  time: number;
  employees: Array<string>; //List of employee ids
  imagePath?: string; // Use the image URL relative to the db
  category?: string; // Optional category for the service
}

export interface PartialService {
  id: string;
  serviceTitle: string;
  serviceAmount: number;
  serviceTime: number;
}

// Use ISO (YYYY-MM-DD via formatDateToISO) for metadata on recurring definitions only.
// Blockers/appointments still rely on legacy MMDDYYYY strings elsewhere, so keeping the formats
// distinct helps us evolve the template layer without rewriting historic schedule docs.
export type IsoDateString = string;

export interface RecurringBreak {
  id: string;
  weekday: number;           // 0-6 (Sunday-Saturday)
  startTime: string;         // HHmm
  endTime: string;           // HHmm
  repeatEveryXWeeks: number; // 1-4
  repeatUntilDate?: IsoDateString;
  indefinite: boolean;
}

export interface Employee {
  id: string;
  admin: boolean;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  appointments: { [date: string]: string[] }; //AppointmentIds
  schedules?: Record<string, string>; // Add this line to store schedules
  profilePictureImagePath?: string; // Use the image URL relative to the db
  bio?: string;
  recurringBreaks?: RecurringBreak[];
  bookable?: boolean;
  isOrchestrated?: boolean;
  active?: boolean;
  socials?: {
    instagram?: string;
    facebook?: string;
    twitter?: string;
  };
}

export interface Schedule {
  id: string;
  time: string; // The string that represents the BITSTRING_LENGTH availability
}

export type ScheduleCacheUpdateInput = {
  employeeId: string;
  appointmentDate: string;
  appointmentTime: string;
  appointmentDuration: number;
  cancel?: boolean;
};

export interface WorkHours {
  monday: string, //todo maybe make a schedule type?
  tuesday: string, //bitstring
  wednesday: string,
  thursday: string,
  friday: string,
  saturday: string,
  sunday: string
}

export enum AppointmentStatus {
  UPCOMING = 'UPCOMING',
  PENDING_PAYMENT = 'PENDING_PAYMENT',
  COMPLETE = 'COMPLETE',
  CANCELLED = 'CANCELLED',
  NON_APPOINTMENT = 'NON_APPOINTMENT',
  INTERNAL_BOOKING = 'INTERNAL_BOOKING',
}

export interface Appointment {
  id: string;
  employeeID: string;
  employeeName: string;
  employeeEmail: string;
  employeePhoneNumber: string;
  customerFirstName: string;
  customerLastName: string;
  customerPhoneNumber: string;
  customerEmail: string;
  billingAmount: number;
  creationTime: Timestamp;
  appointmentDate: string; // MMDDYYYY
  appointmentTime: string;
  status: AppointmentStatus;
  appointmentTitle: string;
  appointmentDuration: number; //Amount in minutes
  servicesList: Array<PartialService>;
  businessName: string; // The name of the business that the appointment is for
  businessUrl?: string; // The URL of the business that the appointment is for
  lastUpdater?: string; // The ID of the employee who last updated the appointment or 'customer'
  allowSendCustomerSMS: boolean;
  allowSendEmployeeSMS: boolean;
  notes?: string; // Optional short notes from the customer (max 160 chars)
  recurringBreakId?: string; // Optional: If this appointment object is a recurring break, the ID of the break definition
  paid?: boolean; // Optional: Indicates if the appointment has been paid for
  paymentInfo?: {
    paymentIntentId?: string;
    amountCents?: number;
    serviceAmountCents?: number;
    tipAmountCents?: number;
    refunded?: boolean;
    refundedAmountCents?: number;
    refundId?: string;
    refundReason?: string | null;
    refundStatus?: string;
    refundedAt?: number;
    confirmedAt?: number;
    lastAttemptAt?: number;
    errorCode?: string;
    declineCode?: string | null;
  };
  tags?: string[]; // Optional: appointment metadata flags (e.g., internal booking)
}

export function createEmptyAppointment(partialAppointment: Partial<Appointment> = {}): Appointment {
  return {
    id: '',
    employeeID: '',
    employeeName: '',
    employeeEmail: '',
    employeePhoneNumber: '',
    customerFirstName: '',
    customerLastName: '',
    customerPhoneNumber: '',
    customerEmail: '',
    billingAmount: 0,
    creationTime: new Timestamp(0, 0),
    appointmentDate: '',
    appointmentTime: '',
    status: AppointmentStatus.UPCOMING,
    appointmentTitle: '',
    appointmentDuration: 0,
    servicesList: [],
    businessName: '',
    businessUrl: `${window.location.protocol}//${window.location.host}`,
    lastUpdater: '',
    allowSendCustomerSMS: false,
    allowSendEmployeeSMS: false,
    notes: '',
    recurringBreakId: undefined,
    tags: [],
    ...partialAppointment, // override with what's passed in
  };
}

export type StatsDocNumbers = {
  upcomingCount: number;
  completedCount: number;
  canceledCount: number;
  upcomingRevenueCents: number;
  completedRevenueCents: number;
  canceledRevenueCents: number;
  upcomingMinutes: number;
  completedMinutes: number;
  canceledMinutes: number;
};

export const emptyStatsAccumulator = (): StatsDocNumbers => ({
  upcomingCount: 0,
  completedCount: 0,
  canceledCount: 0,
  upcomingRevenueCents: 0,
  completedRevenueCents: 0,
  canceledRevenueCents: 0,
  upcomingMinutes: 0,
  completedMinutes: 0,
  canceledMinutes: 0,
});

export const classifyStatus = (
  status: AppointmentStatus
): 'upcoming' | 'completed' | 'canceled' | 'ignore' => {
  switch (status) {
    case AppointmentStatus.COMPLETE:
      return 'completed';
    case AppointmentStatus.CANCELLED:
      return 'canceled';
    default:
      return 'ignore';
  }
};

export type StatsWrite = {
  ref: any;
  data: StatsDocNumbers & { key: string; updatedAt: any };
};

export const INTERNAL_APPOINTMENT_TAG = 'internal-booking';

export const appointmentHasTag = (
  appointment: Pick<Appointment, 'tags'> | null | undefined,
  tag: string
): boolean => !!appointment?.tags?.includes(tag);

export const isBlocker = (appointment?: Pick<Appointment, 'status'> | null | undefined): boolean =>
  appointment?.status === AppointmentStatus.NON_APPOINTMENT;

export const isUpcomingStatus = (status: AppointmentStatus): boolean =>
  status === AppointmentStatus.UPCOMING || status === AppointmentStatus.INTERNAL_BOOKING;

export const isRealAppointment = (appointment?: Pick<Appointment, 'status'> | null | undefined): boolean =>
  !!appointment && appointment.status !== AppointmentStatus.NON_APPOINTMENT;

export const isActiveAppointment = (appointment?: Appointment | null | undefined): appointment is Appointment =>
  !!appointment && isUpcomingStatus(appointment.status) && !isBlocker(appointment);

export const occupiesSchedule = (appointment?: Appointment | null | undefined): appointment is Appointment => {
  if (!appointment) return false;
  if (isBlocker(appointment)) return true;
  return appointment.status !== AppointmentStatus.CANCELLED && appointment.status !== AppointmentStatus.COMPLETE;
};

export interface BookingConfirmation extends Appointment {
  employee: string;
}

export interface CustomerIdentity {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export type CustomerCreateInput = CustomerIdentity & {
  employeeIds?: string[];
};

export interface Customer extends CustomerIdentity {
  totalAppointments: number;
  totalCompletedRevenue: number;
  lastAppointmentDate: Timestamp | null;
  employeeIds: string[];
}

// data thats embeded in a bit string
export interface BitStringData {
  bitString: string;
  startTime: Dayjs;
  endTime: Dayjs;
  isDayOff: boolean;
}

export interface EmployeeInvitation {
  accessCode: string;
  expiryTime: Timestamp;
  services: Array<string>; // array of ids
  isAdmin: boolean;
  websiteId: string;
}

// This is whats in firebase
interface GalleryImage {
  imagePath: string; // Use the image URL relative to the db
  title?: string;
  description?: string;
}

interface buisnessHours {
  monday: string;
  tuesday: string;
  wednesday: string;
  thursday: string;
  friday: string;
  saturday: string;
  sunday: string;
}

// This is what we use interally
export interface GalleryItem {
  imagePath: string;
  imageURL?: string;
  title?: string;
  description?: string;
}

export interface HomepageReview {
  name: string;
  text: string;
  rating?: number; // out of 5, defaults to 5 when missing
}

export type BookingModel = 'staff' | 'resource';

export interface BusinessUiFlags {
  showHomepageTeamSection?: boolean;
  showHomepageReviewsSection?: boolean;
  showHomepageGallerySection?: boolean;
  showHomepageAboutSection?: boolean;
  showEmployeeBio?: boolean;
  showEmployeeSocials?: boolean;
  showEmployeeProfilePhoto?: boolean;
  showEmployeePickerInBooking?: boolean;
}

export interface BusinessTerminology {
  resourceSingular?: string;
  resourcePlural?: string;
  anyAvailableLabel?: string;
  selectResourceLabel?: string;
}

// Changes in this also need to be reflected in the stripeWebHook.ts
export type SubscriptionStatus = 'active' | 'canceled' | 'unpaid';

export type LogoPlacement = 'navbar' | 'heroOverlay' | 'both';
export type PreviewSiteType = 'barbershop' | 'nailsalon';


// If you add a new section to the business config you must
// Update the following :
// - createEmptyBusinessConfig (for any changes)
// - get config and updateBusinessConfig if you add a new section
// - src/screens/SuperAdmin/BusinessConfigPage.tsx
// - validation schema in BusinessConfigPage.tsx
export interface BusinessConfig {
  businessInfo: {
    name: string;
    address: string;
    phone: string;
    email?: string;
    websiteDomain?: string;
    timezone: string;
    logoImagePath?: string;
    businessHours: buisnessHours;
  };
  socials: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };
  homepage: {
    heroImagePath: string;
    heroTitle: string;
    heroSubtitle: string;
    // announcements: Array<string>;
    aboutUs?: string;
    gallery?: Array<GalleryImage>; //array of image paths
    reviews?: Array<HomepageReview>;
    googleMapsSrc: string;
    heroLogoImagePath?: string;
    logoPlacement: 'navbar' | 'heroOverlay' | 'both';
  };
  // Add any feature flags or settings here
  features: {
    employeeCount?: number;
    loginlessEmployeeCount?: number;
    enableCustomerSMS: boolean;
    enableEmployeeSMS: boolean;
    enablePayments: boolean;
    enableCart?: boolean;
  };

  // Payment and subscription details in regards to subscripiton to the site
  paymentInfo?: {
    owner: string;             // Firebase UID of the owner
    skipSubscriptionCheck?: boolean; // Optional: If true, subscription is not required for full functionality
    planType?: string;        // e.g., 'free', 'basic', 'premium'
    billingModeApplied?: 'employees' | 'front_desk'; // Optional: Tracks the last subscription mode applied by Stripe billing
    stripeCustomerId?: string;    // Optional: Set when Stripe customer is created
    stripeSubscriptionId?: string;// Optional: Set when subscription is active
    subscriptionStatus?: string;  // e.g., 'active', 'trialing', 'past_due', 'canceled'
    // trialEndsAt?: number;         // Optional: Unix timestamp (ms) when free trial ends
    currentPeriodEnd?: number;    // Optional: Billing period end (from Stripe)
    lastUpdated?: number;         // Optional: For tracking cache freshness
    hasUsedTrial?: boolean; // Optional: To track if the user has used the trial
  },
  payoutInfo?: {
    useBusinessStripeAccount: boolean;
    businessStripeAccountId?: string; // only set if `useBusinessStripeAccount` is true
  };
  preferences?: {
    serviceCategories?: string[]; // Optional: Array of service categories for filtering
    requirePaymentForBooking?: boolean; // Optional: Whether payment is required to complete a booking
    allowTips?: boolean; // Optional: Whether customers can add a tip during card checkout
    minimumBookingNoticeHours?: number; // Optional: Minimum notice (in hours) before a booking can be made
    enableEmployeeEmails?: boolean; // Optional: Enable employee-facing appointment emails for this business
    allowAnyAvailableEmployee?: boolean; // Optional: Whether customers can choose the "Any Available" option
    bookingModel?: BookingModel; // Optional: Selects customer-booking UI behavior mode
    uiFlags?: BusinessUiFlags; // Optional: Frontend visibility/behavior flags
    terminology?: BusinessTerminology; // Optional: Frontend terminology overrides
    orchestratorMode?: boolean; // Optional: Route customer bookings through orchestrator flow
    primaryOrchestratorId?: string; // Optional: Employee ID to receive orchestrator notifications
    lastEmployeeScheduleMaintenanceAt?: number; // Optional: Last time site-wide schedule maintenance completed
    nextEmployeeScheduleMaintenanceAt?: number; // Optional: Next time site-wide schedule maintenance is due
  }

}

export interface PreviewSiteConfig {
  siteType: PreviewSiteType;
  primaryColor: string;
  secondaryColor: string;
  businessLogoPath: string;
  heroTitle: string;
  heroSubtitle: string;
  googleMapEmbed: string;
}

export const PREVIEW_SITE_DEFAULT_MAP_EMBEDS: Record<PreviewSiteType, string> = {
  nailsalon: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5862.895039642988!2d-71.1136476!3d42.7154178!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89e30691641aed8f%3A0xd62041947314ac6a!2sTran%20Tran%20Nails!5e0!3m2!1sen!2sus!4v1777733001675!5m2!1sen!2sus',
  barbershop: 'https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d249.3636450214468!2d0!3d0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89e3719eb4292ebd%3A0xb744974c52670269!2sBeacon%20Hill%20Barbershops!5e0!3m2!1sen!2sus!4v1778619122219!5m2!1sen!2sus',
};

export function createEmptyPreviewSiteConfig(siteType: PreviewSiteType = 'barbershop'): PreviewSiteConfig {
  return {
    siteType,
    primaryColor: '#000000',
    secondaryColor: '#FFFFFF',
    businessLogoPath: '',
    heroTitle: '',
    heroSubtitle: '',
    googleMapEmbed: PREVIEW_SITE_DEFAULT_MAP_EMBEDS[siteType],
  };
}

export function createEmptyBusinessConfig(): BusinessConfig {
  return {
    businessInfo: {
      name: '',
      address: '',
      phone: '',
      email: '',
      websiteDomain: '',
      timezone: '',
      logoImagePath: '',
      businessHours: {
        monday: '9:00AM - 5:00PM',
        tuesday: '9:00AM - 5:00PM',
        wednesday: '9:00AM - 5:00PM',
        thursday: '9:00AM - 5:00PM',
        friday: '9:00AM - 5:00PM',
        saturday: 'CLOSED',
        sunday: 'CLOSED',
      }
    },
    socials: {
      facebook: '',
      instagram: '',
      twitter: '',
    },
    homepage: {
      heroImagePath: '',
      heroTitle: '',
      heroSubtitle: '',
      aboutUs: '',
      gallery: [],
      reviews: [],
      googleMapsSrc: '',
      heroLogoImagePath: '',
      logoPlacement: 'navbar',
    },
    features: {
      employeeCount: 1,
      loginlessEmployeeCount: 1,
      enableCustomerSMS: false,
      enableEmployeeSMS: false,
      enablePayments: false,
      enableCart: false,
    },
    paymentInfo: {
      owner: '',
    },
    payoutInfo: {
      useBusinessStripeAccount: false,
    },
    preferences: {
      serviceCategories: [],
      requirePaymentForBooking: false,
      allowTips: false,
      minimumBookingNoticeHours: 1,
      enableEmployeeEmails: false,
      allowAnyAvailableEmployee: false,
      bookingModel: 'staff',
      uiFlags: {
        showHomepageTeamSection: true,
        showHomepageReviewsSection: true,
        showHomepageGallerySection: true,
        showHomepageAboutSection: true,
        showEmployeeBio: true,
        showEmployeeSocials: true,
        showEmployeeProfilePhoto: true,
        showEmployeePickerInBooking: true,
      },
      terminology: {
        resourceSingular: 'Employee',
        resourcePlural: 'Employees',
        anyAvailableLabel: 'Any Available',
        selectResourceLabel: 'Select employee',
      },
      orchestratorMode: false,
      primaryOrchestratorId: '',
      lastEmployeeScheduleMaintenanceAt: 0,
      nextEmployeeScheduleMaintenanceAt: 0,
    }
  };
}

export const appointmentKey = (siteId: string, id: string) =>
  ["appointment", siteId, id] as const;
